/* eslint-disable no-constant-condition */
import { ExclamationCircleFilled, FileTextOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Select, Space, Spin, Tag, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import CustomModal from "components/CustomModal";
import {
  CONTRACT_SKIP_LIMIT,
  PRODUCT_TYPE,
  STOCK_STATUS,
  USER_PERMISSIONS,
  USER_STATUS,
  USER_TYPE,
} from "config/constants";
import { ORDER_STATUS, SHIPPING_VERSION } from "features/Order/constants";
import {
  isOrderTypeCustomer,
  isShippingTypeBus,
  isShippingTypeDeliver3Party,
  isShippingTypePickUpCounter,
  isShippingTypeUrbanCOD,
  isStockStatusFull,
} from "features/Order/helper";
import { useFlags } from "flagsmith/react";
import { useGetInfoPlatform } from "hooks/info-platform";
import { useApproveStock, useConfirmExportOrder } from "hooks/order";
import useDebounce from "hooks/useDebounce";
import { useGetMe, useGetShipper } from "@monorepo/hooks";
import { t } from "i18next";
import { CustomModalApprovePayment } from "pages/App/Order/List/components/CustomModalApprovePayment";
import CustomModalSpiltOrder from "pages/App/Order/List/components/CustomModalSpiltOrder/index ";
import CustomModalWarning from "pages/App/Order/List/components/CustomModalWarning";
import { DELIVER_ORDER_STATUS, WARNING_MODAL_ACTION } from "pages/App/Order/conts";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import ListReasonApproveOrder from "../ListReasonApproveOrder";
import "./index.scss";
import { ButtonAssignToCustomer } from "./ButtonAssignToCustomer";
import { ButtonAssignToDeliver } from "./ButtonAssignToDeliver";
import { ButtonChangeShipper } from "./ButtonChangeShipper";
import { ButtonDivisionOrder } from "./ButtonDivisonOrder";
import { ButtonSuccessfulDelivery } from "./ButtonSuccessfulDelivery";

const ButtonApproveOrder = ({
  handle,
  preOrder,
  loadingApprove,
  orderID,
  chatURL,
  deliveryDeadline,
  warehouseName,
  warehouseNameID,
  warehouses,
  dataSource,
  messages,
  seller,
  warehouseBranch,
  potentialCustomer,
  promotionType,
  maxDeliveryDate,
  minDeliveryDate,
  orderData,
  receiverContact,
  paymentMethod,
  sellerID,
  needsContractWarning,
  shippingType,
  pickupInfo,
}) => {
  const history = useHistory();
  const [form] = useForm();
  const [formValues, setFormValues] = useState({});
  const [visible, setVisible] = useState(false);
  const [visibleContractWarning, setVisibleContractWarning] = useState(false);
  const [warehouseID, setWarehouseID] = useState();
  const [addressApproved, setAddressApproved] = useState(false);
  const [selectedPickup, setSelectedPickup] = useState({
    warehouseID: pickupInfo?.warehouseID,
    receiverID: pickupInfo?.receiver?.id,
  });

  const contractSkipCount = useMemo(() => Math.max(Number(seller?.contractSkipRemaining) || 0, 0), [seller]);
  const contractSkipTotal = Math.max(CONTRACT_SKIP_LIMIT, contractSkipCount);
  const contractSkippedCount = Math.max(contractSkipTotal - contractSkipCount, 0);

  const openApproveModal = () => {
    setVisible(true);
  };

  const handleOpen = () => {
    if (needsContractWarning === true) {
      setVisibleContractWarning(true);
      return;
    }
    openApproveModal();
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleCloseContractWarning = () => {
    setVisibleContractWarning(false);
  };

  const handleGoToCreateContract = () => {
    const selectedSellerID = sellerID || seller?.id;
    history.push({
      pathname: "/contract/create",
      search: `?sellerId=${selectedSellerID}`,
      state: {
        preselectedSeller: selectedSellerID
          ? {
              value: selectedSellerID,
              label: seller?.fullName,
            }
          : undefined,
      },
    });
  };

  const handleSkipContractAndApprove = () => {
    handleCloseContractWarning();
    openApproveModal();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const formValues = form.getFieldsValue();

      await handle({
        warehouseID: warehouseID === warehouseNameID ? undefined : warehouseID,
        paymentMethod: formValues.paymentMethod,
        deadlineDelivery: formValues.deliveryDeadline?.toISOString(),
        internalNote: formValues.internalNote,
        pickupWarehouseID: selectedPickup?.warehouseID,
        pickupReceiverID: selectedPickup?.receiverID,
      });

      handleClose();
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };
  const isDisabled =
    (formValues?.paymentMethod === "DEBT" || paymentMethod === "DEBT") &&
    receiverContact?.status === "WAITING_FOR_APPROVAL" &&
    !addressApproved;

  return (
    <>
      <Button type="primary" onClick={handleOpen}>
        {t("order.approvalOrder.title")}
      </Button>

      <CustomModal
        footer={false}
        centered={true}
        disabled={isDisabled}
        onOke={handleSubmit}
        visible={visible}
        onCancel={handleClose}
        title={<Typography.Title level={3}>{t("order.approvalOrder.title")}</Typography.Title>}
        width="70%"
      >
        <ListReasonApproveOrder
          loadingApprove={loadingApprove}
          chatURL={chatURL}
          deliveryDeadline={deliveryDeadline}
          preOrder={preOrder}
          orderID={orderID}
          form={form}
          warehouseName={warehouseName}
          warehouseNameID={warehouseNameID}
          warehouses={warehouses}
          dataSource={dataSource}
          setWarehouseID={setWarehouseID}
          messages={messages}
          seller={seller}
          warehouseBranch={warehouseBranch}
          potentialCustomer={potentialCustomer}
          promotionType={promotionType}
          maxDeliveryDate={maxDeliveryDate}
          minDeliveryDate={minDeliveryDate}
          orderData={orderData}
          receiverContact={receiverContact}
          paymentMethod={paymentMethod}
          addressApproved={addressApproved}
          setAddressApproved={setAddressApproved}
          sellerID={sellerID}
          onFormValuesChange={setFormValues}
          shippingType={shippingType}
          pickupInfo={pickupInfo}
          onPickupChange={(pickup) => setSelectedPickup(pickup)}
        />
      </CustomModal>

      <CustomModal
        footer={false}
        centered={true}
        visible={visibleContractWarning}
        onCancel={handleCloseContractWarning}
        hideCancelButton={true}
        hideConfirmButton={true}
      >
        <div
          style={{
            background: "#FFFFFF",
            borderRadius: 18,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
            padding: 24,
          }}
        >
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Space direction="vertical" size={12} style={{ width: "100%", alignItems: "center" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#FFF3E0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ExclamationCircleFilled style={{ fontSize: 30, color: "#FA8C16" }} />
              </div>
              <Typography.Title level={4} style={{ textAlign: "center", marginBottom: 0 }}>
                Đại lý cần ký hợp đồng trước khi duyệt đơn hàng
              </Typography.Title>
            </Space>

            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Typography.Text style={{ color: "#4B5563", fontSize: 14, lineHeight: "22px" }}>
                Đơn hàng từ 10.000.000đ trở lên hoặc tổng giá trị đơn hàng của đại lý đã đạt 20.000.000đ.
              </Typography.Text>
              <Typography.Text style={{ color: "#4B5563", fontSize: 14, lineHeight: "22px" }}>
                Vui lòng thực hiện ký hợp đồng với đại lý trước khi tiếp tục giao dịch.
              </Typography.Text>
            </Space>

            <div
              onClick={handleGoToCreateContract}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleGoToCreateContract();
                }
              }}
              style={{
                border: "1px solid #E0E0E0",
                borderRadius: 12,
                padding: "14px 16px",
                cursor: "pointer",
              }}
            >
              <Row align="middle" justify="space-between" wrap={false} gutter={12}>
                <Col flex="auto">
                  <Space align="start" size={12}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "#E8F5E9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FileTextOutlined style={{ color: "#2E7D32", fontSize: 16 }} />
                    </div>
                    <Space direction="vertical" size={0}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        Thêm hợp đồng
                      </Typography.Text>
                      <Typography.Text style={{ fontSize: 13, color: "#6B7280" }}>
                        Liên hệ đại lý ký hợp đồng và thêm hợp đồng cho đại lý
                      </Typography.Text>
                    </Space>
                  </Space>
                </Col>
                <Col>
                  <RightOutlined style={{ color: "#9CA3AF", fontSize: 14 }} />
                </Col>
              </Row>
            </div>

            <Row gutter={12}>
              <Col span={12}>
                <Button
                  style={{ width: "100%", height: 42, borderRadius: 10, borderColor: "#1976D2", color: "#1976D2" }}
                  onClick={handleCloseContractWarning}
                >
                  Hủy bỏ
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  style={{ width: "100%", height: 42, borderRadius: 10, background: "#1976D2" }}
                  onClick={handleSkipContractAndApprove}
                  disabled={contractSkipCount <= 0}
                >
                  Bỏ qua, tiếp tục duyệt đơn
                </Button>
              </Col>
            </Row>

            <div style={{ width: "100%", textAlign: "right" }}>
              <Typography.Text style={{ fontSize: 12, color: "#D32F2F" }}>Chỉ cho phép bỏ qua {contractSkipTotal} lần. </Typography.Text>
              <Typography.Text style={{ fontSize: 12, color: "#111827" }}>
                Bạn đã bỏ qua {contractSkippedCount}/{contractSkipTotal}
              </Typography.Text>
            </div>
          </Space>
        </div>
      </CustomModal>
    </>
  );
};

// const ButtonEditOrder = ({ handle }) => {
//   // const flags = useFlags(["is_hide_edit_order"]);
//   // const isHideEditOrder = !flags.is_hide_edit_order.enabled;
//   return (
//     // isHideEditOrder || (
//     //   <Button onClick={handle}>
//     //     <Typography.Text strong>{t("order.orderDetail.editBtn")}</Typography.Text>
//     //   </Button>
//     // )
//     <Button onClick={handle}>
//       <Typography.Text strong>{t("order.orderDetail.editBtn")}</Typography.Text>
//     </Button>
//   );
// };

const ButtonWaitingForEnoughStock = ({ handle, stockStatus }) => {
  return isStockStatusFull(stockStatus) ? (
    <CustomModal
      footer={false}
      centered={true}
      customComponent={<Button>{t("order.orderStatus.waitingForEnoughStock")}</Button>}
      onOke={handle}
    >
      <Row gutter={[16, 8]}>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <ExclamationCircleFilled style={{ fontSize: "48px", color: "#ff7f00" }} />
        </Col>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <Typography.Text strong>{t("order.changeOrderStatusWarning.waitingStockDetail")}</Typography.Text>
        </Col>
      </Row>
    </CustomModal>
  ) : (
    <Button onClick={handle}>
      <Typography.Text strong>{t("order.orderStatus.waitingForEnoughStock")}</Typography.Text>
    </Button>
  );
};

const ButtonUnableToImport = ({ handle, stockStatus }) => {
  return isStockStatusFull(stockStatus) ? (
    <CustomModal
      footer={false}
      centered={true}
      customComponent={<Button>{t("order.orderDetail.unableToImport")}</Button>}
      onOke={handle}
    >
      <Row gutter={[16, 8]}>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <ExclamationCircleFilled style={{ fontSize: "48px", color: "#ff7f00" }} />
        </Col>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <Typography.Text strong>{t("order.changeOrderStatusWarning.denyStockDetail")}</Typography.Text>
        </Col>
      </Row>
    </CustomModal>
  ) : (
    <Button onClick={handle}>
      <Typography.Text strong>{t("order.orderDetail.unableToImport")}</Typography.Text>
    </Button>
  );
};

const ButtonSufficientConfirmation = ({ handle }) => {
  return (
    <Button onClick={handle} type="primary">
      <Typography.Text strong style={{ color: "#ffffff" }}>
        {t("order.orderDetail.sufficientConfirmation")}
      </Typography.Text>
    </Button>
  );
};

const ButtonApprovePayment = ({ handle }) => {
  return (
    <CustomModal
      footer={false}
      centered={true}
      customComponent={<Button type="primary">{t("order.orderDetail.approvedPayment")}</Button>}
      onOke={handle}
    >
      <Row gutter={[16, 8]}>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <ExclamationCircleFilled style={{ fontSize: "48px", color: "#ff7f00" }} />
        </Col>
        <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
          <Typography.Text strong>{t("order.orderList.confirmPaymentWarning")}</Typography.Text>
        </Col>
      </Row>
    </CustomModal>
  );
};

const ButtonPrintAndPack = ({ onPrint, handle, order }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const keyLicenseProducts = order?.items?.filter(
    (item) => item.product.productType === PRODUCT_TYPE.KEY_LICENSE || item.product.productType === PRODUCT_TYPE.ACCOUNT
  );

  const handleCancel = () => setIsVisible(false);

  const handleOk = async () => {
    try {
      setLoading(true);
      await onPrint()
        .then(async () => {
          notify.success({ message: "In đơn thành công" });
          await handle()
            .then(() => {
              // notify.success({ message: "Đóng gói thành công" });
              setLoading(false);
              handleCancel();
            })
            .catch((error) => {
              setLoading(false);
              notify.error({ message: error.message });
            });
        })
        .catch((error) => {
          setLoading(false);
          notify.error({ message: error.message });
        });
    } catch (error) {
      console.info(error);
    }
  };

  return (
    <>
      <Button onClick={() => setIsVisible(true)} type="primary" loading={loading}>
        {t("order.orderDetail.printAndPackage")}
      </Button>
      <CustomModal
        footer={false}
        centered={true}
        visible={isVisible}
        closable={false}
        buttonLoading={loading}
        selfVisibleControlled={false}
        className="confirm-pack-modal"
        customIcon={<ExclamationCircleFilled style={{ color: "rgba(239, 147, 81, 1)", fontSize: "40px" }} />}
        title={
          <Typography.Text strong style={{ fontSize: "16px" }}>
            {t("order.packagingOrder.title")}
          </Typography.Text>
        }
        message={
          <Typography.Text type="warning">
            {keyLicenseProducts?.length
              ? `Lưu ý: Đơn hàng có chứa ${keyLicenseProducts?.length} sản phẩm key license/ account nên một phần đơn hàng sẽ được chuyển sang bộ phận kho Digital xử lý!`
              : `Lưu ý: Sau khi Xác nhận, đơn hàng sẽ được chuyển sang trạng thái “Chờ xuất kho”`}
          </Typography.Text>
        }
        onCancel={handleCancel}
        onOke={handleOk}
      />
    </>
  );
};

const ButtonApproveExport = ({ handle, order, canSuggestKeyLicenseAccountConfirm, canExportStock }) => {
  const queryParams = new URLSearchParams();
  queryParams.set("orderId", JSON.stringify(order?.id));
  const { handleConfirmExportOrder } = useConfirmExportOrder();
  const { platformCode } = useGetInfoPlatform();

  const flags = useFlags(["is_redirect_export_serial"]);
  const listPlatformCode = JSON.parse(flags.is_redirect_export_serial?.value)?.platformCode;

  const handleExportConfirm = async () => {
    await handleConfirmExportOrder({
      orderIDs: [order?.id],
    })
      .then(() => {
        notify.success({
          message: t("order.orderList.exportingSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.exportingFail"),
          description: error.message,
        });
      });
  };

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "checkReload") {
        window.location.reload();
        localStorage.removeItem("checkReload");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    var retrievedUser = JSON.parse(localStorage.getItem("checkReload"));
    if (retrievedUser) {
      localStorage.removeItem("checkReload");
      window.location.reload();
    }
  }, []);

  const hasDigitalProduct = order?.items?.some(
    (item) =>
      item?.product?.productType === PRODUCT_TYPE.KEY_LICENSE || item?.product?.productType === PRODUCT_TYPE.ACCOUNT
  );

  const handleClick = () => {
    if (hasDigitalProduct) {
      if (canSuggestKeyLicenseAccountConfirm) {
        const url = `/create-export-key#${order?.id}`;
        window.open(url, "_blank");
      } else {
        notify.warning({
          message: t("order.orderDetail.warning_export_product_digital"),
        });
      }
    } else {
      if (canExportStock) {
        const url = `/export-serial/create?${queryParams.toString()}`;
        window.open(url, "_blank");
      } else {
        notify.warning({
          message: t("order.orderDetail.warning_export_product_material"),
        });
      }
    }
  };

  return listPlatformCode?.includes(platformCode) ? (
    <Button type="primary" onClick={handleClick}>
      <Typography.Text strong style={{ color: "#ffffff" }}>
        {t("order.orderDetail.exportConfirmation")}
      </Typography.Text>
    </Button>
  ) : (
    <Button type="primary" onClick={handleExportConfirm}>
      <Typography.Text strong style={{ color: "#ffffff" }}>
        {t("order.orderDetail.exportConfirmation")}
      </Typography.Text>
    </Button>
  );
};

const ButtonApproveAssembly = ({ handle }) => {
  return (
    <Button onClick={handle} type="primary">
      <Typography.Text strong style={{ color: "#ffffff" }}>
        {t("order.orderList.confirmAssemble")}
      </Typography.Text>
    </Button>
  );
};

const ButtonAssignToShipper = ({ handle }) => {
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const { me } = useGetMe();
  const sellerID = me?.seller?.id;

  const [params, setParams] = useState({
    filters: {
      query: undefined,
      permission: USER_PERMISSIONS.ORDER_SHIPPER_DELIVER,
      userType: USER_TYPE.PARTNER_STAFF,
      status: USER_STATUS.ACTIVE,
    },
    pagination: {
      offset: 0,
      limit: 10,
    },
  });

  const { shippers, loading: loadingGetShipper } = useGetShipper({
    ...params,
    filters: { ...params.filters, sellerID },
    skip: !sellerID,
  });
  const debounceValue = useDebounce(searchTerm);

  const renderTag = (inDeliverOrders) => {
    if (!inDeliverOrders?.length) {
      return <Tag color="green">{t("order.orderList.orderNotAssigned")}</Tag>;
    }
    return (
      <Space>
        {inDeliverOrders.map(({ status, count }) =>
          status === DELIVER_ORDER_STATUS.WAIT_TO_DELIVER ? (
            <Tag key={status} color="orange">
              {t("order.orderList.orderWaitAssigned")} {count}
            </Tag>
          ) : (
            <Tag key={status} color="red">
              {t("order.orderList.orderAssigned")} {count}
            </Tag>
          )
        )}
      </Space>
    );
  };

  useEffect(() => {
    setParams({
      ...params,
      filters: {
        ...params.filters,
        query: debounceValue,
      },
      pagination: {
        limit: 10,
        offset: 0,
      },
    });
  }, [debounceValue]);

  const onSubmit = async () => {
    const formValues = form.getFieldsValue();
    await handle(formValues?.shipperID);
  };

  return (
    <CustomModal
      title={<Typography.Text strong>{t("order.selectingShipper.title")}</Typography.Text>}
      footer={false}
      centered={true}
      customComponent={
        <Button style={{ width: "100%" }} type="primary">
          {t("order.orderList.confirmShipping")}
        </Button>
      }
      onOke={onSubmit}
    >
      <Form form={form}>
        <Form.Item
          label={<Typography.Text strong>{t("order.selectingShipper.shipperLabel")}</Typography.Text>}
          name="shipperID"
          rules={[
            {
              required: true,
              message: t("order.selectingShipper.shipperValidateMessage"),
            },
          ]}
        >
          <Select
            showSearch
            placeholder={t("order.selectingShipper.shipperPlaceholder")}
            filterOption={false}
            optionLabelProp="label"
            onSearch={(value) => setSearchTerm(value)}
            dropdownRender={(originNode) => <Spin spinning={loadingGetShipper}>{originNode}</Spin>}
          >
            {shippers?.map(({ value, name, label, inDeliverOrders }) => (
              <Select.Option key={value} value={value} label={name}>
                <Space style={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography.Text>{label}</Typography.Text>
                  {renderTag(inDeliverOrders)}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

const ButtonRecreateOrder = ({ handle }) => {
  return (
    <Button onClick={handle} type="primary">
      <Typography.Text strong style={{ color: "#ffffff" }}>
        {t("order.orderDetail.recreateCart")}
      </Typography.Text>
    </Button>
  );
};

const ButtonGroup = ({ dataButtonGroup, actions, order }) => {
  const {
    onWaitingForEnoughStock,
    onDeny,
    onApproveStock,
    onExportOrderWithPrint,
    onPackage,
    onExportConfirm,
    onAssemblyConfirm,
    onConfirmShipper,
    onConfirmCustomer,
    onRecreateOrder,
    onEditOrder,
    onApproveOrder,
    onChangeShipper,
  } = actions;
  const {
    status,
    preOrder,
    stockStatus,
    shippingType,
    canApproveStock,
    canAssignShipper,
    canCompleteAssembly,
    canApproveOrder,
    canCompleteOrder,
    canConfirmPayment,
    canDenyStock,
    canExportStock,
    canRecreateCart,
    canWaitStock,
    id,
    exportedWarehouse,
    number,
    serviceFee,
    discount,
    shippingFee,
    paymentMethod,
    items,
    orderType,
    shippingStatus,
    shippingVersion,
    chatURL,
    deliveryDeadline,
    loadingApproveOrder,
    canSuggestKeyLicenseAccountConfirm,
    seller,
    messages,
    potentialCustomer,
    receiverContact,
    sellerID,
    pickupInfo,
  } = dataButtonGroup;

  const [openModalWarningApproval, setOpenModalWarningApproval] = useState(false);
  const { handleApproveStock } = useApproveStock();
  const handleApprovalStock = async () => {
    if (order.stockStatus !== STOCK_STATUS.FULL) {
      setOpenModalWarningApproval(true);
    } else {
      await handleApproveStock({
        orderIDs: [order?.id],
      })
        .then(() => {
          notify.success({
            message: t("order.orderList.approvedEnoughStockSuccess"),
          });
        })
        .catch((error) => {
          notify.error({
            message: t("order.orderList.approvedEnoughStockError"),
            description: error.message,
          });
        });
    }
  };

  const allOrderItems = items.flatMap((order) => (order.orderItems?.length ? order.orderItems : [order]));

  const renderButtonGroupByStatus = {
    [`${ORDER_STATUS.WAITING_FOR_APPROVAL_ORDER}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canApproveOrder && (
          <ButtonApproveOrder
            handle={onApproveOrder}
            preOrder={preOrder}
            chatURL={chatURL}
            deliveryDeadline={deliveryDeadline}
            loadingApprove={loadingApproveOrder}
            orderID={id}
            warehouseName={exportedWarehouse?.name}
            warehouseNameID={exportedWarehouse?.id}
            warehouses={seller?.warehouses}
            dataSource={allOrderItems}
            messages={messages}
            seller={seller}
            warehouseBranch={exportedWarehouse.branch}
            potentialCustomer={potentialCustomer}
            promotionType={order?.promotionType}
            maxDeliveryDate={order?.maxDeliveryDate}
            minDeliveryDate={order?.minDeliveryDate}
            receiverContact={receiverContact}
            paymentMethod={paymentMethod}
            onAddressApproved={onApproveOrder}
            sellerID={sellerID}
            needsContractWarning={order?.needsContractWarning}
            shippingType={shippingType}
            pickupInfo={pickupInfo}
            orderData={{
              paymentMethod: paymentMethod,
              deliveryDeadline: deliveryDeadline,
            }}
          />
        )}
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_APPROVAL_STOCK}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canWaitStock && <ButtonWaitingForEnoughStock handle={onWaitingForEnoughStock} stockStatus={stockStatus} />}
        <CustomModalSpiltOrder
          items={items}
          labelButton={t("order.splitOrder.confirmAndApprovedEnoughStock")}
          infoOrder={{
            id,
            nameStock: exportedWarehouse?.name,
            orderNumber: number,
            serviceFee: serviceFee,
            discount: discount,
            shippingFee: shippingFee,
            paymentMethod: paymentMethod,
          }}
          isHidden={!isOrderTypeCustomer(orderType)}
        />
        {canApproveStock && (
          <>
            <Button type="primary" className="btn-main-action" onClick={handleApprovalStock}>
              {t("order.orderList.approvedEnoughStock")}
            </Button>
            {openModalWarningApproval && (
              <CustomModalWarning
                openModalWarning={openModalWarningApproval}
                setOpenModalWarning={setOpenModalWarningApproval}
                orderIDs={[]}
                action={WARNING_MODAL_ACTION.APPROVAL_STOCK_LIST}
              />
            )}
          </>
        )}
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_ENOUGH_STOCK}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canDenyStock && <ButtonUnableToImport handle={onDeny} stockStatus={stockStatus} />}
        {canApproveStock && <ButtonSufficientConfirmation handle={onApproveStock} />}

        <CustomModalSpiltOrder
          items={items}
          labelButton={t("order.splitOrder.confirmAndApprovedEnoughStock")}
          infoOrder={{
            id,
            nameStock: exportedWarehouse?.name,
            orderNumber: number,
            serviceFee: serviceFee,
            discount: discount,
            shippingFee: shippingFee,
            paymentMethod: paymentMethod,
          }}
          isHidden={!isOrderTypeCustomer(orderType)}
        />
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_PAYMENT}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canConfirmPayment && <CustomModalApprovePayment orderIDs={[order?.id]} orderDetail={order} />}
        <CustomModalSpiltOrder
          items={items}
          labelButton={t("order.splitOrder.confirmAndApprovedEnoughStock")}
          infoOrder={{
            id,
            nameStock: exportedWarehouse?.name,
            orderNumber: number,
            serviceFee: serviceFee,
            discount: discount,
            shippingFee: shippingFee,
            paymentMethod: paymentMethod,
          }}
          isHidden={!isOrderTypeCustomer(orderType)}
        />
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_PACKAGING}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        <ButtonPrintAndPack onPrint={onExportOrderWithPrint} handle={onPackage} order={order} />
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_EXPORTING}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {(canExportStock || canSuggestKeyLicenseAccountConfirm) && (
          <ButtonApproveExport
            handle={onExportConfirm}
            order={order}
            canSuggestKeyLicenseAccountConfirm={canSuggestKeyLicenseAccountConfirm}
            canExportStock={canExportStock}
          />
        )}
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_ASSEMBLY}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canCompleteAssembly && <ButtonApproveAssembly handle={onAssemblyConfirm} />}
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_SHIPPING}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {isShippingTypePickUpCounter(shippingType) && (
          <ButtonAssignToCustomer handle={onConfirmCustomer} orderDetail={order} />
        )}
        {isShippingTypeDeliver3Party(shippingType) && shippingVersion === SHIPPING_VERSION.V1 && (
          <ButtonAssignToDeliver orderDetail={order} />
        )}
        {(isShippingTypeUrbanCOD(shippingType) || isShippingTypeBus(shippingType)) && canAssignShipper && (
          <ButtonAssignToShipper handle={onConfirmShipper} />
        )}
        {(isShippingTypeBus(shippingType) || isShippingTypeDeliver3Party(shippingType)) &&
          shippingVersion === SHIPPING_VERSION.V2 && <ButtonDivisionOrder order={order} />}
      </>
    ),
    [`${ORDER_STATUS.WAITING_FOR_SHIPPING + "WAITING"}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {isShippingTypePickUpCounter(shippingType) && (
          <ButtonAssignToCustomer handle={onConfirmCustomer} orderDetail={order} />
        )}
        {(isShippingTypeBus(shippingType) || isShippingTypeDeliver3Party(shippingType)) && canAssignShipper && (
          <ButtonChangeShipper order={order} handle={onChangeShipper} />
        )}
        {isShippingTypeDeliver3Party(shippingType) && <ButtonAssignToDeliver orderDetail={order} />}
      </>
    ),

    [`${ORDER_STATUS.DELIVERING}`]: (
      <>
        {/* <ButtonEditOrder handle={onEditOrder} /> */}
        {canCompleteOrder && <ButtonSuccessfulDelivery orderDetail={order} />}
      </>
    ),
    [`${ORDER_STATUS.COMPLETED}`]: canRecreateCart && <ButtonRecreateOrder handle={onRecreateOrder} />,
    [`${ORDER_STATUS.CANCELED}`]: canRecreateCart && <ButtonRecreateOrder handle={onRecreateOrder} />,
  };

  return (
    <Space style={{ background: "#FFFFFF", width: "100%", display: "flex", justifyContent: "end", padding: "5px" }}>
      {
        renderButtonGroupByStatus[
          `${status === ORDER_STATUS.WAITING_FOR_SHIPPING ? `${status}${shippingStatus ?? ""}` : status}`
        ]
      }
    </Space>
  );
};

export default ButtonGroup;
